'use client';

import { useState, useEffect } from 'react';
import { RefreshCw, ArrowUpDown, Info, DollarSign, Euro, PoundSterling, JapaneseYen, IndianRupee, Bitcoin, Loader2 } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { currencyConverterContent } from '../../data/currency-converter-content';
import Link from 'next/link';

// List of popular currencies with their symbols and flags (using emoji for simplicity)
interface Currency {
    code: string;
    name: string;
    symbol: string;
    flag: string;
    decimals?: number;
}

const CURRENCIES: Currency[] = [
    { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸', decimals: 2 },
    { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺', decimals: 2 },
    { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧', decimals: 2 },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥', flag: '🇯🇵', decimals: 0 },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', flag: '🇦🇺', decimals: 2 },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', flag: '🇨🇦', decimals: 2 },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', flag: '🇨🇭', decimals: 2 },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', flag: '🇨🇳', decimals: 2 },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹', flag: '🇮🇳', decimals: 2 },
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', flag: '🇸🇬', decimals: 2 },
    { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', flag: '🇳🇿', decimals: 2 },
    { code: 'KRW', name: 'South Korean Won', symbol: '₩', flag: '🇰🇷', decimals: 0 },
    { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', flag: '🇧🇷', decimals: 2 },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R', flag: '🇿🇦', decimals: 2 },
    { code: 'RUB', name: 'Russian Ruble', symbol: '₽', flag: '🇷🇺', decimals: 2 },
    { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$', flag: '🇲🇽', decimals: 2 },
    { code: 'BTC', name: 'Bitcoin', symbol: '₿', flag: '₿', decimals: 8 },
    { code: 'ETH', name: 'Ethereum', symbol: 'Ξ', flag: 'Ξ', decimals: 18 },
];

// Exchange rate API configuration
const EXCHANGE_API_URL = 'https://api.exchangerate.host/latest';
const FALLBACK_RATES = {
    USD: 1,
    EUR: 0.93,      // 1 USD = 0.93 EUR
    GBP: 0.79,      // 1 USD = 0.79 GBP
    JPY: 150.50,    // 1 USD = 150.50 JPY
    AUD: 1.52,      // 1 USD = 1.52 AUD
    CAD: 1.36,      // 1 USD = 1.36 CAD
    CHF: 0.90,      // 1 USD = 0.90 CHF
    CNY: 7.25,      // 1 USD = 7.25 CNY
    INR: 83.30,     // 1 USD = 83.30 INR (approximately 1 INR = 0.012 USD)
    SGD: 1.35,      // 1 USD = 1.35 SGD
    NZD: 1.67,      // 1 USD = 1.67 NZD
    KRW: 1315.00,   // 1 USD = 1315.00 KRW
    BRL: 4.95,      // 1 USD = 4.95 BRL
    ZAR: 18.50,     // 1 USD = 18.50 ZAR
    RUB: 90.00,     // 1 USD = 90.00 RUB
    MXN: 17.20,     // 1 USD = 17.20 MXN
    BTC: 0.000025,  // 1 USD = 0.000025 BTC
    ETH: 0.00042,   // 1 USD = 0.00042 ETH
};

interface ExchangeRates {
    [key: string]: number;
}

interface ExchangeRateResponse {
    success: boolean;
    base: string;
    date: string;
    rates: ExchangeRates;
}

// Get exchange rate between two currencies
const getExchangeRate = async (from: string, to: string, amount: number): Promise<number> => {
    if (from === to) return amount;

    // Get the rates for both currencies from FALLBACK_RATES
    const fromRate = FALLBACK_RATES[from as keyof typeof FALLBACK_RATES];
    const toRate = FALLBACK_RATES[to as keyof typeof FALLBACK_RATES];

    if (fromRate === undefined || toRate === undefined) {
        throw new Error(`Missing exchange rate for ${from} or ${to}`);
    }

    // Calculate the conversion rate and return the converted amount
    const rate = toRate / fromRate;
    return amount * rate;
};


export default function CurrencyConverter() {
    const [amount, setAmount] = useState<string>('1');
    const [fromCurrency, setFromCurrency] = useState<string>('USD');
    const [toCurrency, setToCurrency] = useState<string>('EUR');
    const [result, setResult] = useState<number>(0);
    const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [exchangeRate, setExchangeRate] = useState<number>(1);
    const [inverseRate, setInverseRate] = useState<number>(1);

    // Get currency details by code
    const getCurrencyDetails = (code: string) => {
        return CURRENCIES.find(currency => currency.code === code) || CURRENCIES[0];
    };

    // Format currency with proper symbol and decimal places
    const formatCurrency = (value: number, currencyCode: string) => {
        try {
            const currency = getCurrencyDetails(currencyCode);
            const defaultDecimals = currencyCode === 'JPY' || currencyCode === 'KRW' ? 0 : 2;
            const maxDecimals = currency?.decimals ?? defaultDecimals;

            // Use a default format if Intl.NumberFormat fails
            const formatter = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currencyCode,
                minimumFractionDigits: 0,
                maximumFractionDigits: Math.min(Math.max(0, maxDecimals), 20), // Ensure valid range
            });
            return formatter.format(value);
        } catch (error) {
            // Fallback formatting if Intl.NumberFormat fails
            console.warn(`Error formatting currency ${currencyCode}:`, error);
            const defaultDecimals = currencyCode === 'JPY' || currencyCode === 'KRW' ? 0 : 2;
            const formattedValue = value.toFixed(defaultDecimals);
            const symbol = CURRENCIES.find(c => c.code === currencyCode)?.symbol || currencyCode;
            return `${symbol}${formattedValue}`;
        }
    };

    // Format the last updated time in a consistent 
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    const formatLastUpdated = (date: Date) => {
        if (!isClient) {
            // Return a placeholder during SSR to prevent hydration mismatch
            return 'Last updated: --:--:--';
        }

        // Format date as YYYY-MM-DD HH:MM:SS in 24-hour format
        const pad = (num: number) => num.toString().padStart(2, '0');
        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1);
        const day = pad(date.getDate());
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());
        const seconds = pad(date.getSeconds());
        return `Last updated: ${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    // Convert currencies and update exchange rates
    const convert = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const amountNum = parseFloat(amount);
            if (isNaN(amountNum)) {
                setError('Please enter a valid number');
                return;
            }

            // Get the conversion rate
            const rate = await getExchangeRate(fromCurrency, toCurrency, 1);
            setExchangeRate(rate);
            setInverseRate(1 / rate);

            // Calculate the converted amount
            const converted = amountNum * rate;
            setResult(converted);
            setLastUpdated(new Date());
        } catch (err) {
            setError('An error occurred while converting. Please try again.');
            console.error('Conversion error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Swap currencies
    const swapCurrencies = () => {
        const temp = fromCurrency;
        setFromCurrency(toCurrency);
        setToCurrency(temp);
        // Trigger conversion after swap
        convert();
    };

    // Update result when inputs change
    useEffect(() => {
        // Don't run on initial render
        if (isClient) {
            const timer = setTimeout(() => {
                convert();
            }, 500); // Add debounce to prevent too many API calls

            return () => clearTimeout(timer);
        }
    }, [fromCurrency, toCurrency, amount, isClient]);

    // Format exchange rate with appropriate decimal places
    const formatExchangeRate = (rate: number, currencyCode: string) => {
        // For very small rates (like 1 USD to BTC), show more decimal places
        if (rate < 0.01) return rate.toFixed(8);
        // For rates less than 1, show up to 6 decimal places
        if (rate < 1) return rate.toFixed(6);
        // For rates between 1 and 100, show 4 decimal places
        if (rate < 100) return rate.toFixed(4);
        // For rates 100 or more, show 2 decimal places
        return rate.toFixed(2);
    };

    // Format the result with the target currency symbol
    const formatResult = () => {
        if (isLoading) return 'Converting...';
        if (error) return 'Error';
        return formatCurrency(result, toCurrency);
    };

    // Get currency symbol
    const getSymbol = (code: string) => {
        const currency = getCurrencyDetails(code);
        return currency ? currency.symbol : '';
    };

    // Get currency flag emoji
    const getFlag = (code: string) => {
        const currency = getCurrencyDetails(code);
        return currency ? currency.flag : '';
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
            <div className="max-w-3xl mx-auto">
                <div className="text-center mb-8">
                    <ToolPageIntro title="Currency Converter">
                Convert between 150+ world currencies with real-time exchange rates. Our free currency converter helps you get accurate conversions for travel, business, and finance.
            </ToolPageIntro>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8">
                    <div className="space-y-6">
                        {/* Amount Input */}
                        <div>
                            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Amount
                            </label>
                            <div className="relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-gray-500 dark:text-gray-400 text-lg">
                                        {getSymbol(fromCurrency)}
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    id="amount"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-12 py-3 sm:text-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                                    placeholder="0.00"
                                    inputMode="decimal"
                                />
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                    <span className="text-gray-500 dark:text-gray-400 text-lg">
                                        {getFlag(fromCurrency)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Currency Selectors */}
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center justify-center z-10">
                                <button
                                    onClick={swapCurrencies}
                                    className="p-2 bg-white dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                                    aria-label="Swap currencies"
                                >
                                    <ArrowUpDown className="h-5 w-5" />
                                </button>
                            </div>

                            <div className="space-y-4 relative z-0">
                                {/* From Currency */}
                                <div>
                                    <label htmlFor="fromCurrency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        From
                                    </label>
                                    <select
                                        id="fromCurrency"
                                        value={fromCurrency}
                                        onChange={(e) => {
                                            setFromCurrency(e.target.value);
                                            // Don't convert here to avoid double conversion from useEffect
                                        }}
                                        className="mt-1 block w-full pl-3 pr-10 py-3 text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm rounded-lg cursor-pointer appearance-none"
                                    >
                                        {CURRENCIES.map((currency) => (
                                            <option key={`from-${currency.code}`} value={currency.code}>
                                                {currency.code} - {currency.name} {currency.flag}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* To Currency */}
                                <div>
                                    <label htmlFor="toCurrency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        To
                                    </label>
                                    <select
                                        id="toCurrency"
                                        value={toCurrency}
                                        onChange={(e) => {
                                            setToCurrency(e.target.value);
                                            // Don't convert here to avoid double conversion from useEffect
                                        }}
                                        className="mt-1 block w-full pl-3 pr-10 py-3 text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm rounded-lg cursor-pointer appearance-none"
                                    >
                                        {CURRENCIES.map((currency) => (
                                            <option key={`to-${currency.code}`} value={currency.code}>
                                                {currency.code} - {currency.name} {currency.flag}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Result */}
                        <div className="mt-8">
                            <div className="bg-blue-50 dark:bg-blue-900/30 p-6 rounded-xl border border-blue-100 dark:border-blue-800">
                                <div className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                                    {amount || '0'} {fromCurrency} =
                                </div>
                                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                                    {formatResult()}
                                </div>
                                <div className="mt-2 text-sm text-blue-600 dark:text-blue-400">
                                    1 {fromCurrency} = {isLoading ? '...' : formatExchangeRate(exchangeRate, toCurrency)} {toCurrency}
                                </div>
                                <div className="mt-1 text-sm text-blue-600 dark:text-blue-400">
                                    1 {toCurrency} = {isLoading ? '...' : formatExchangeRate(inverseRate, fromCurrency)} {fromCurrency}
                                </div>
                            </div>
                        </div>

                        {/* Last Updated */}
                        <div className="text-right text-xs text-gray-500 dark:text-gray-400 mt-2">
                            <div className="flex items-center justify-end">
                                <RefreshCw className={`h-3 w-3 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                                <span>
                                    {isLoading ? 'Updating...' : formatLastUpdated(lastUpdated)}
                                </span>
                            </div>
                        </div>

                        {error && (
                            <div className="text-red-600 dark:text-red-400 text-sm mt-2">
                                {error}
                            </div>
                        )}
                    </div>
                </div>

                {/* Popular Currency Conversions */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Popular Conversions
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {['USD', 'EUR', 'GBP', 'JPY', 'INR', 'AUD'].map((baseCurrency) => (
                            baseCurrency !== fromCurrency && baseCurrency !== toCurrency && (
                                <button
                                    key={`convert-${baseCurrency}`}
                                    onClick={() => {
                                        setFromCurrency(fromCurrency);
                                        setToCurrency(baseCurrency);
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                    className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left"
                                >
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                        {getFlag(fromCurrency)} 1 {fromCurrency} → ... {baseCurrency}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        {getFlag(toCurrency)} 1 {toCurrency} → ... {baseCurrency}
                                    </div>
                                </button>
                            )
                        ))}
                    </div>
                </div>

                {/* Info Section */}
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded-r">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <Info className="h-5 w-5 text-yellow-400" />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">About Currency Conversion</h3>
                            <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                                <p>• Exchange rates are for informational purposes only.</p>
                                <p>• Actual rates may vary depending on the financial institution.</p>
                                <p>• Cryptocurrency rates are highly volatile.</p>
                                <p>• {formatLastUpdated(lastUpdated)}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Informational Content */}
                <div className="mt-12">
                    <InformationalContent
                        title={currencyConverterContent.title}
                        description={currencyConverterContent.description}
                        content={currencyConverterContent.content}
                        faqs={currencyConverterContent.faqs}
                        relatedTools={currencyConverterContent.relatedTools}
                    />
                </div>
            </div>
        </div>
    );
}
